import { MutableRefObject, useLayoutEffect, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

type UseDimensionOptions = { includePadding?: boolean };
const initialState = { width: 0, height: 0 };
/**
 * Pass a ref to an element to watch its width and height.
 *
 * **Usage:**
 * ```
 * const divRef = useRef(null);
 * const { height, width } = useDimension(divRef);
 * <div ref={divRef} />
 * ```
 *
 * If you want the size to include padding, you can pass includePadding on the options:
 *
 * ```
 * const { height, width } = useDimension(divRef, {includePadding: true});
 * ```
 */
export const useDimension = (
  ref: MutableRefObject<HTMLElement | null>,
  { includePadding = false }: UseDimensionOptions = {}
) => {
  const [dimensions, setDimensions] = useState(initialState);

  const observer = new ResizeObserver((entries: ResizeObserverEntry[] = []) => {
    entries.forEach((entry) => {
      let width, height;
      if (includePadding) {
        const { inlineSize, blockSize } = entry.borderBoxSize[0];
        width = inlineSize;
        height = blockSize;
      } else {
        width = entry.contentRect.width;
        height = entry.contentRect.height;
      }
      setDimensions({ width, height });
    });
  });
  const resizeObserverRef = useRef<ResizeObserver>(observer);

  useLayoutEffect(
    () => {
      const observerRef = resizeObserverRef.current;
      if (!ref.current) {
        return;
      }
      observerRef.observe(ref.current);

      return () => {
        if (!observerRef) {
          return;
        }
        observerRef.disconnect();
      };
    },
    // we need to watch ref.current instead of ref, otherwise the observer can report an inaccurate initial width
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ref.current]
  );
  return dimensions;
};
